import React, { useEffect, useState } from "react";
import { Card, Button, Accordion, Form } from "react-bootstrap";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { DateTime } from "luxon";
import MeetingCard from "../components/MeetingCard";
import PastRecordings from "../components/PastRecordings";

const ZOOM_SESSIONS_URL =
  "https://opensheet.elk.sh/11R9T54CjmC2RFZuvmD7fxElItomKuvfTb9t5nHjeWNU/ZoomSessions";
const COURSES_URL =
  "https://opensheet.elk.sh/11R9T54CjmC2RFZuvmD7fxElItomKuvfTb9t5nHjeWNU/Courses";
const READING_URL =
  "https://opensheet.elk.sh/11R9T54CjmC2RFZuvmD7fxElItomKuvfTb9t5nHjeWNU/ReadingMaterials";

const courseColors = {
  mm: "#f8b195",   // existing
  mem: "#355c7d",  // existing
  fra: "#6c5b7b",  // existing
  me: "#f67280",   // soft coral pink
  or: "#c06c84",   // muted mauve
  igd: "#99b898",  // soft green
  sm: "#fcd5ce",   // warm peach
};
const formatSessionTime = (dateStr, timeStr, userTz) => {
  // dateStr: "27-May-25"
  // timeStr: "8pm - 10pm" or "11:30am - 1:30pm"
  const [startRaw, endRaw] = timeStr.toLowerCase().split(" - ");
  const startTime = parseTime(startRaw, dateStr, userTz);
  const endTime = parseTime(endRaw, dateStr, userTz);

  const formattedDate = startTime.toFormat("dd-MMM-yy");
  const formattedRange = `${startTime.toFormat("h:mma")} - ${endTime.toFormat(
    "h:mma"
  )}`;

  return { formattedDate, formattedRange };
};
const parseTime = (time, dateStr, userTz) => {
  // Normalize time input
  let normalizedTime = time.trim().toLowerCase();
  normalizedTime = normalizedTime.replace(/am|pm/, (match) =>
    match.toUpperCase()
  );

  if (!normalizedTime.includes(":")) {
    normalizedTime = normalizedTime.replace(/(AM|PM)$/, ":00$1");
  }

  const dateTimeFormats = ["dd-MMM-yy h:mma", "d-MMM-yy h:mma"];
  const dateTimeStr = `${dateStr} ${normalizedTime}`;

  let dt;
  for (const format of dateTimeFormats) {
    dt = DateTime.fromFormat(dateTimeStr, format, {
      zone: "Asia/Kolkata",
    });
    if (dt.isValid) break;
  }

  if (!dt || !dt.isValid) {
    console.error("Failed to parse datetime:", dateTimeStr);
    return null;
  }

  return dt.setZone(userTz);
};
const generateColorFromString = (() => {
  const colorMap = {}; // store courseId -> color
  const usedHues = new Set(); // track hues to avoid duplicates

  return (courseId) => {
    if (!courseId) return "#8884d8";

    const key = courseId.toLowerCase();
    if (colorMap[key]) return colorMap[key];

    // Generate a consistent hash from the string
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Generate a hue from the hash (0-360)
    let hue = Math.abs(hash) % 360;

    // Ensure uniqueness by slightly shifting if hue already used
    let attempt = 0;
    while (usedHues.has(hue) && attempt < 20) {
      hue = (hue + 37) % 360; // arbitrary step to reduce collisions
      attempt++;
    }
    usedHues.add(hue);

    // Convert HSL to hex for visually distinct color
    const saturation = 65; // percent
    const lightness = 55; // percent
    const color = hslToHex(hue, saturation, lightness);

    colorMap[key] = color;
    return color;
  };
})();
const parseDDMMMYY = (dateStr) => {
  const [day, monStr, yearStr] = dateStr.split("-");
  const monthMap = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3,
    May: 4, Jun: 5, Jul: 6, Aug: 7,
    Sep: 8, Oct: 9, Nov: 10, Dec: 11,
  };

  const year = Number(yearStr) + 2000; // 25 → 2025
  const month = monthMap[monStr];

  return new Date(year, month, Number(day));
};

// Helper to convert HSL to HEX
function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) =>
    Math.round(
      255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1))))
    );
  return `#${[f(0), f(8), f(4)]
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("")}`;
}

const Dashboard = () => {
  const [today, setToday] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [pastWithRecording, setPastWithRecording] = useState([]);
  const [courseMeta, setCourseMeta] = useState({});
  const [readingMap, setReadingMap] = useState({});
  const [courseOptions, setCourseOptions] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("All");
  const [showJoinPrompt, setShowJoinPrompt] = useState(false);
  const [joinLink, setJoinLink] = useState(null);
  const [examSessions, setExamSessions] = useState([]);
  const [midTermCountdown, setMidTermCountdown] = useState(null);
  const [midTermCountdownTime, setMidTermCountdownTime] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const [sessionsRes, coursesRes, readingsRes] = await Promise.all([
        fetch(ZOOM_SESSIONS_URL),
        fetch(COURSES_URL),
        fetch(READING_URL),
      ]);
      const [sessions, courses, readings] = await Promise.all([
        sessionsRes.json(),
        coursesRes.json(),
        readingsRes.json(),
      ]);

      const courseDict = {};
      courses.forEach((course) => {
        courseDict[course.CourseId] = {
          email: course.FacultyEmail,
          phone: course.FacultyNumber,
        };
      });
      setCourseMeta(courseDict);
      setCourseOptions(["All", ...Object.keys(courseDict)]);

      const readingDict = {};
      readings.forEach((item) => {
        const cid = item.CourseId;
        if (!readingDict[cid]) readingDict[cid] = [];
        readingDict[cid].push({
          title: item.Title,
          link: item.ReadingMaterialLink,
        });
      });
      setReadingMap(readingDict);

      const now = new Date();
      const isSameLocalDate = (date1, date2) =>
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate();

      const filteredToday = sessions.filter((s) => {
        const sessionDate = new Date(s.Date);
        return isSameLocalDate(sessionDate, now);
      });

      const filteredUpcoming = sessions.filter((s) => {
        const sessionDate = new Date(s.Date);
        return sessionDate >= now;
      });

      const filteredPastWithRecording = sessions.filter((s) => {
        const sessionDate = new Date(s.Date);
        return (
          sessionDate < now && s.RecordingLink && s.RecordingLink.trim() !== ""
        );
      });

      const examRelatedSessions = sessions.filter((s) =>
        s.Class?.toLowerCase().includes("exam")
      );
      
      const futureExamSessions = examRelatedSessions.filter((s) => {
        const dt = DateTime.fromFormat(s.Date, "d-MMM-yy");
        return dt.isValid && dt > DateTime.now();
      });
      
      setExamSessions(futureExamSessions);
      const upcomingExams = examRelatedSessions
        .map((s) => ({
          ...s,
          parsedDate: DateTime.fromFormat(s.Date, "d-MMM-yy"),
        }))
        .filter((s) => s.parsedDate > DateTime.now())
        .sort((a, b) => a.parsedDate - b.parsedDate);

      if (upcomingExams.length > 0) {
        const nextExamDate = upcomingExams[0].parsedDate;
        const diff = nextExamDate.diffNow(['days', 'hours', 'minutes']).toObject();
        const formatted =
          `${Math.floor(diff.days)}d ` +
          `${String(Math.floor(diff.hours)).padStart(2, '0')}h ` +
          `${String(Math.floor(diff.minutes)).padStart(2, '0')}m`;
        setMidTermCountdown(formatted);
      }
      setToday(filteredToday);
      setUpcoming(filteredUpcoming);
      setPastWithRecording(filteredPastWithRecording);
    };

    fetchData();
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();

      today.forEach((session) => {
        const [startTime] = session.Time.split("-");
        const trimmedStartTime = startTime.trim();

        const timeMatch = trimmedStartTime.match(
          /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i
        );
        if (!timeMatch) return;

        let hours = parseInt(timeMatch[1], 10);
        const minutes = parseInt(timeMatch[2], 10);
        const meridian = timeMatch[3].toUpperCase();

        if (meridian === "PM" && hours !== 12) hours += 12;
        if (meridian === "AM" && hours === 12) hours = 0;

        if (hours === currentHours && minutes === currentMinutes) {
          setJoinLink(decodeGoogleRedirect(session["Meeting Link"]));
          setShowJoinPrompt(true);
        }
      });
    }, 45000);

    return () => clearInterval(interval);
  }, [today]);
  useEffect(() => {
    let interval;

    const updateCountdown = () => {
      if (examSessions.length === 0) return;

      const upcomingExams = examSessions
        .map((s) => {
          const [hourStr, minuteStr] = (s.Time || "00:00").split(":");
          const parsedDate = DateTime.fromFormat(s.Date, "d-MMM-yy").set({
            hour: parseInt(hourStr) || 0,
            minute: parseInt(minuteStr) || 0,
          });
          return { ...s, parsedDate };
        })
        .filter((s) => s.parsedDate > DateTime.now())
        .sort((a, b) => a.parsedDate - b.parsedDate);

      if (upcomingExams.length > 0) {
        const nextExam = upcomingExams[0].parsedDate;
        const now = DateTime.now();
        const diff = nextExam
          .diff(now, ["days", "hours", "minutes", "seconds"])
          .toObject();

        const formatted =
          `${Math.floor(diff.days)}:` +
          `${String(Math.floor(diff.hours)).padStart(2, "0")}:` +
          `${String(Math.floor(diff.minutes)).padStart(2, "0")}:` +
          `${String(Math.floor(diff.seconds)).padStart(2, "0")}`;

        setMidTermCountdownTime(formatted);
      } else {
        setMidTermCountdownTime(null);
      }
    };

    updateCountdown();
    interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [examSessions]);

  const decodeGoogleRedirect = (url) => {
    try {
      const parsedUrl = new URL(url);
      if (
        parsedUrl.hostname === "www.google.com" &&
        parsedUrl.pathname === "/url"
      ) {
        const actualUrl = parsedUrl.searchParams.get("q");
        return actualUrl ? decodeURIComponent(actualUrl) : url;
      }
    } catch (e) {}
    return url;
  };

  const normalizeTitle = (title) =>
    title
      ?.replace(/^\d+\.\s*/, "")
      .replace(/\.[^/.]+$/, "")
      .toLowerCase()
      .trim();

  const formatRecordingLink = (url) => {
    if (!url) return "";
    const match = url.match(
      /https:\/\/drive\.google\.com\/file\/d\/([^/]+)\/(view|preview)/
    );
    if (match) return `https://drive.google.com/file/d/${match[1]}/preview`;
    return url;
  };

  const handleVideoClick = (url) => {
    window.open(formatRecordingLink(url), "_blank", "noopener,noreferrer");
  };

  const renderPrereads = (session) => {
    const courseId = session.CourseId;
    const prereadsRaw = session.Prereads?.trim();
    if (!prereadsRaw) return null;

    const prereads = prereadsRaw
      .split("\n")
      .map((p) => p.trim())
      .filter(Boolean);
    if (prereads.length === 0) return null;

    const readings = readingMap[courseId] || [];

    const matchedLinks = prereads.map((pre, idx) => {
      const normPre = normalizeTitle(pre);
      const match = readings.find((r) =>
        normalizeTitle(r.title).includes(normPre)
      );
      return match ? (
        <li key={`match-${idx}`}>
          <a href={match.link} target="_blank" rel="noreferrer">
            {match.title}
          </a>
        </li>
      ) : (
        <li key={`unmatched-${idx}`}>{pre}</li>
      );
    });

    return (
      <div>
        <strong>Prereads:</strong>
        <ul>{matchedLinks}</ul>
      </div>
    );
  };

  const renderSessionCard = (session) => {
    if (selectedCourse !== "All" && session.CourseId !== selectedCourse)
      return null;

    const meta = courseMeta[session.CourseId] || {};
    return (
      <MeetingCard
        key={session.Class}
        session={session}
        meta={meta}
        renderPrereads={renderPrereads}
        formatSessionTime={formatSessionTime}
        decodeGoogleRedirect={decodeGoogleRedirect}
        handleVideoClick={handleVideoClick}
      />
    );
  };

  const renderAccordionSection = (title, sessions) =>
    sessions.length === 0 ? null : (
      <Accordion defaultActiveKey="0" className="mb-4">
        <Accordion.Item eventKey="0">
          <Accordion.Header>{title}</Accordion.Header>
          <Accordion.Body>
            {sessions.map((s) => renderSessionCard(s))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );

  const renderUpcomingSessionsAccordion = (sessions) => (
    <>
      <h2 className="mt-4">Upcoming Sessions</h2>
      <Accordion alwaysOpen className="mb-4">
        {sessions
          .filter(
            (s) => selectedCourse === "All" || s.CourseId === selectedCourse
          )
          .map((session, index) => {
            const bgColor =
              generateColorFromString[session.CourseId?.toLowerCase()] || "#ffffff";
            const textColor = "#fff";

            return (
              <Accordion.Item
                eventKey={String(index)}
                key={session.Class + index}
              >
                <Accordion.Header
                  style={{
                    "--bs-accordion-btn-bg": bgColor,
                    "--bs-accordion-btn-color": textColor,
                  }}
                >
                  <div className="d-flex flex-column w-100">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{session.CourseName}</strong> —{" "}
                        {(() => {
                          const userTz =
                            localStorage.getItem("userTimezone") ||
                            "Asia/Kolkata";
                          const { formattedDate, formattedRange } =
                            formatSessionTime(
                              session.Date,
                              session.Time,
                              userTz
                            );
                          return (
                            <>
                              <strong>Date:</strong> {formattedDate} |{" "}
                              <strong>Time:</strong> {formattedRange}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>{renderSessionCard(session)}</Accordion.Body>
              </Accordion.Item>
            );
          })}
      </Accordion>
    </>
  );
  const renderGroupedUpcomingSessions = (sessions) => {
    const userTz = localStorage.getItem("userTimezone") || "Asia/Kolkata";
    const grouped = groupSessionsByDate(
      sessions.filter((s) => selectedCourse === "All" || s.CourseId === selectedCourse),
      userTz
    );
  
    return (
      <>
        <h2 className="mt-4">Upcoming Sessions</h2>
        <Accordion alwaysOpen className="mb-4">
          {Object.entries(grouped)
            .sort(([dateA], [dateB]) => parseDDMMMYY(dateA) - parseDDMMMYY(dateB))
            .map(([date, dateSessions], index) => {
            const summary = dateSessions.map((s) => {
              const { formattedRange } = formatSessionTime(s.Date, s.Time, userTz);
              return {
                courseId: s.CourseId,
                courseName: s.CourseName,
                time: formattedRange,
              };
            });
  
            return (
              <Accordion.Item eventKey={String(index)} key={date}>
                <Accordion.Header>
                  <div className="d-flex flex-column w-100">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{date}</strong> — {dateSessions.length} Session{dateSessions.length > 1 ? 's' : ''}
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {summary.map((s, i) => {
                          const bg = generateColorFromString(s.courseId?.toLowerCase()) || "#ccc";
                          console.log(s.courseId+" "+bg);
                          return (
                            <span
                              key={i}
                              style={{
                                backgroundColor: bg,
                                color: "white",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                fontSize: "0.8rem",
                              }}
                            >
                              {s.courseId.toUpperCase()} ({s.time})
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {dateSessions.map((s) => renderSessionCard(s))}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </>
    );
  };
  
  const handlePieSliceClick = (data) => {
    if (!data) return;
    setSelectedCourse(data.name);
  };

  const getCourseSessionData = (sessions) => {
    const countMap = {};
    sessions.forEach((s) => {
      const courseId = s.CourseId?.toLowerCase() || "unknown";
      countMap[courseId] = (countMap[courseId] || 0) + 1;
    });

    const total = sessions.length;

    return Object.entries(countMap).map(([courseId, count]) => ({
      name: courseId.toUpperCase(),
      value: count,
      color: generateColorFromString(courseId) || "#8884d8",
    }));
  };
  const groupSessionsByDate = (sessions, userTz) => {
    const grouped = {};
  
    sessions.forEach((session) => {
      const { formattedDate } = formatSessionTime(session.Date, session.Time, userTz);
      if (!grouped[formattedDate]) grouped[formattedDate] = [];
      grouped[formattedDate].push(session);
    });
  
    return grouped;
  };
  const pieData = getCourseSessionData(upcoming);

  return (
    <>
      {midTermCountdownTime !== null && (
        <div
        style={{
          position: "fixed",
          top: "10px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1055,
          backgroundColor: "rgba(220, 53, 69, 0.85)", // Bootstrap red with 85% opacity
          color: "white",
          textAlign: "center",
          padding: "10px 16px",
          fontWeight: "bold",
          fontSize: "16px",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          maxWidth: "90%",
          width: "300px",
        }}
      >
          🕒 Mid Term Exam in {midTermCountdownTime}
        </div>
      )}

      <div
        style={{ display: "flex", alignItems: "flex-start", marginTop: "66px" }}
      >
        <div style={{ flex: 1, paddingRight: "300px" }}>
          <div className="d-flex justify-content-end mb-4">
            <Form.Select
              style={{ width: "200px" }}
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
            >
              {courseOptions.map((courseId) => (
                <option key={courseId} value={courseId}>
                  {courseId}
                </option>
              ))}
            </Form.Select>
          </div>

          {renderAccordionSection("Today's Zoom Sessions", today)}
          {renderGroupedUpcomingSessions(upcoming)}
        </div>

        <div
          style={{
            width: "280px",
            position: "fixed",
            top: "100px",
            right: "10px",
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto",
            background: "#f8f9fa",
            padding: "10px",
            borderRadius: "10px",
            boxShadow: "0 0 8px rgba(0,0,0,0.1)",
          }}
        >
          {examSessions.length > 0 && (
            <div className="mt-4">
              <h5 className="text-danger">📘 Upcoming Exams</h5>
              <ul className="list-group">
                {examSessions.map((exam, idx) => {
                  const userTz =
                    localStorage.getItem("userTimezone") || "Asia/Kolkata";
                  const { formattedDate, formattedRange } = formatSessionTime(
                    exam.Date,
                    exam.Time,
                    userTz
                  );
                  return (
                    <li
                      key={`exam-${idx}`}
                      className="list-group-item d-flex justify-content-between align-items-center bg-warning"
                    >
                      <span>
                        <strong>{exam.CourseName}</strong>: {exam.Class}
                      </span>
                      <span className="badge bg-danger rounded-pill">
                        {formattedDate}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          
          <PastRecordings
            pastWithRecording={pastWithRecording}
            selectedCourse={selectedCourse}
            handleVideoClick={handleVideoClick}
            formatSessionTime={formatSessionTime}
          />
          <div style={{ marginTop: "30px" }}>
            <h6>Upcoming Sessions by Course</h6>
            {pieData.length === 0 ? (
              <p>No sessions to display</p>
            ) : (
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, value }) => `${name}: ${value}`}
                      onClick={handlePieSliceClick}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value} sessions`} />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {showJoinPrompt && joinLink && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered modal-md">
              <div className="modal-content rounded-4 shadow-lg border-0">
                <div className="modal-header border-0 pb-2">
                  <h5 className="modal-title fw-semibold text-primary">
                    Time to Join Zoom
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowJoinPrompt(false)}
                  ></button>
                </div>

                <div className="modal-body pt-0">
                  <p className="mb-3 fs-6 text-muted">
                    Your session is starting now. Click the button below to join
                    the Zoom meeting.
                  </p>
                </div>

                <div className="modal-footer border-0 pt-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowJoinPrompt(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      window.open(joinLink, "_blank", "noopener,noreferrer");
                      setShowJoinPrompt(false);
                    }}
                  >
                    <i className="bi bi-camera-video-fill me-2"></i>
                    Join Zoom Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Dashboard;
